// Modular Firebase SDK imports
// @ts-ignore
import { initializeApp } from "firebase/app";
// @ts-ignore
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  setPersistence,
  browserLocalPersistence,
  indexedDBLocalPersistence,
} from "firebase/auth";

import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";

// Helper to safely access env vars with fallback
// CRITICAL: In production, Vite replaces import.meta.env at build time
const getEnv = (key: string, fallback: string) => {
  try {
    // @ts-ignore
    const envValue = import.meta && import.meta.env && import.meta.env[key];
    if (envValue) {
      // Only log in development - production console should be clean
      if (import.meta.env.DEV) {
        console.log(
          `[FIREBASE CONFIG] Using env var ${key}: ${String(envValue).substring(0, 10)}...`
        );
      }
      return envValue;
    }
    // Only warn in development
    if (import.meta.env.DEV) {
      console.warn(`[FIREBASE CONFIG] ⚠️ Env var ${key} not found, using fallback`);
    }
    return fallback;
  } catch (e) {
    // Only warn in development
    if (import.meta.env.DEV) {
      console.warn(`[FIREBASE CONFIG] ⚠️ Error reading env var ${key}, using fallback:`, e);
    }
    return fallback;
  }
};

// ✅ SINGLE SOURCE OF TRUTH
const firebaseConfig = {
  apiKey: getEnv("VITE_FIREBASE_API_KEY", "AIzaSyDZ_fraiBVFQ2Vu0xSc-WqquE_ijFPgFRg"),

  authDomain: getEnv(
    "VITE_FIREBASE_AUTH_DOMAIN",
    "vitacore-app-335cd.firebaseapp.com"
  ),

  projectId: getEnv(
    "VITE_FIREBASE_PROJECT_ID",
    "vitacore-app-335cd"
  ),

  storageBucket: "vitacore-app-335cd.firebasestorage.app",

  messagingSenderId: getEnv(
    "VITE_FIREBASE_MESSAGING_SENDER_ID",
    "765182523838"
  ),

  appId: getEnv(
    "VITE_FIREBASE_APP_ID",
    "1:765182523838:web:b8c0b17770b50268c3e1ef"
  ),
};


// Debug logs - only in development (production console should be clean)
if (import.meta.env.DEV) {
  console.log("[FIREBASE INIT] ===== FIREBASE INITIALIZATION =====");
  console.log(
    "[FIREBASE INIT] Environment:",
    typeof window !== "undefined" ? window.location.hostname : "server"
  );
  console.log("[FIREBASE INIT] Project ID:", firebaseConfig.projectId);
  console.log("[FIREBASE INIT] Auth Domain:", firebaseConfig.authDomain);
  console.log("[FIREBASE INIT] Storage Bucket:", firebaseConfig.storageBucket);
  // Note: Domain configuration warning removed from production logs
  // Ensure 'vitacore.fitness' is configured in Firebase Console > Authentication > Settings > Authorized domains
  console.log("[FIREBASE INIT] =========================================");
}

// Initialize Firebase - SINGLE INSTANCE
// @ts-ignore
const app = initializeApp(firebaseConfig);
export { app };

// Auth
// @ts-ignore
export const auth = getAuth(app);

// Persistence
setPersistence(auth, indexedDBLocalPersistence).catch((error) => {
  console.warn("[AUTH] indexedDBLocalPersistence failed, falling back to browserLocalPersistence:", error);
  setPersistence(auth, browserLocalPersistence).catch((fallbackError) => {
    console.error("[AUTH] Failed to set persistence (both methods failed):", fallbackError);
  });
});

export const db = getFirestore(app);

// CRITICAL: Global error handler for Firestore uncaught errors
// This prevents "Uncaught Error in snapshot listener" from crashing the app
if (typeof window !== 'undefined') {
  // Catch unhandled Firestore errors
  const originalError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    // Check if this is a Firestore permission-denied error
    const errorMessage = String(message || '');
    const isFirestoreError = errorMessage.includes('Firestore') || 
                             errorMessage.includes('permission-denied') ||
                             (error && typeof error === 'object' && 'code' in error && error.code === 'permission-denied');
    
    if (isFirestoreError) {
      // Silently handle Firestore permission errors - they're expected in some cases
      if (import.meta.env.DEV) {
        console.warn('[FIRESTORE] Global handler caught Firestore error (suppressed):', message);
      }
      // Prevent error from propagating
      return true;
    }
    // Let other errors propagate normally
    if (originalError) {
      return originalError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Also catch unhandled promise rejections from Firestore
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const isFirestoreError = (reason && typeof reason === 'object' && 'code' in reason && reason.code === 'permission-denied') ||
                            (reason && typeof reason === 'object' && 'message' in reason && String(reason.message).includes('Firestore'));
    
    if (isFirestoreError) {
      // Silently handle Firestore permission errors
      if (import.meta.env.DEV) {
        console.warn('[FIRESTORE] Global handler caught unhandled promise rejection (suppressed):', reason);
      }
      event.preventDefault(); // Prevent error from propagating
    }
  });
}

// ✅ Storage: uses firebaseConfig.storageBucket automatically
export const storage = getStorage(app);

// Verify storage is correctly initialized with the right app instance
if (storage.app !== app) {
  console.error("[FIREBASE] ❌ CRITICAL: Storage app instance mismatch!");
  throw new Error("Storage app instance does not match main app instance");
}

// Verification log - only in development
if (import.meta.env.DEV) {
  console.log("[STORAGE VERIFY] ===== FIREBASE STORAGE INITIALIZATION =====");
  console.log("[STORAGE VERIFY] Bucket in use:", storage.app.options.storageBucket);
  console.log("[STORAGE VERIFY] Config bucket (from env/fallback):", firebaseConfig.storageBucket);
  console.log("[STORAGE VERIFY] Project ID:", firebaseConfig.projectId);
  console.log("[STORAGE VERIFY] Auth Domain:", firebaseConfig.authDomain);

  if (!storage.app.options.storageBucket) {
    console.error("[STORAGE VERIFY] ❌ CRITICAL: Storage bucket not configured!");
  } else {
    console.log("[STORAGE VERIFY] ✅ Storage bucket configured:", storage.app.options.storageBucket);
    console.log(
      "[STORAGE VERIFY] ✅ Uploads will use: https://firebasestorage.googleapis.com/v0/b/" +
        storage.app.options.storageBucket +
        "/o"
    );
    // CORS configuration is a backend/infrastructure concern, not frontend code
    // TODO: Configure CORS in Firebase Console or via gsutil:
    // gsutil cors set cors.json gs://vitacore-app-335cd.firebasestorage.app
    console.log("[STORAGE VERIFY] =========================================");
  }
}

// Re-export Auth functions
export { 
  onAuthStateChanged, 
  signOut, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider,
  FacebookAuthProvider,
};

